import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../app.environment';

export type PaymentStatusValue =
    | 'PENDING'
    | 'SUCCESS'
    | 'FAILED'
    | 'EXPIRED'
    | 'CANCELLED';

export interface PaymentStatusUpdate {
    paymentId: string;
    status: PaymentStatusValue;
    message?: string;
    transactionId?: string;
}

@Injectable({
    providedIn: 'root',
})
export class PaymentStatusService implements OnDestroy {
    private readonly statusSubject = new BehaviorSubject<PaymentStatusUpdate | null>(null);
    readonly status$ = this.statusSubject.asObservable();
    private readonly SOCKET_CONNECT_TIMEOUT_MS = 4000;
    private readonly SOCKET_RECONNECT_DELAY_MS = 1500;
    private readonly MAX_SOCKET_RECONNECT_ATTEMPTS = 3;

    private activePaymentId: string | null = null;
    private paymentSocket: WebSocket | null = null;
    private socketConnectTimer: ReturnType<typeof setTimeout> | null = null;
    private isSocketManuallyClosed = false;
    private isSocketConnected = false;
    private isTerminalStateReached = false;

    constructor() { }

    startPaymentTracking(paymentId: string): void {
        this.stopPaymentTracking();

        this.activePaymentId = paymentId;
        this.isTerminalStateReached = false;
        this.statusSubject.next({ paymentId, status: 'PENDING' });
        this.openPaymentSocket(paymentId);
    }

    stopPaymentTracking(): void {
        this.closeSocket();
        this.activePaymentId = null;
        this.isTerminalStateReached = false;
        this.statusSubject.next(null);
    }

    ngOnDestroy(): void {
        this.closeSocket();
        this.statusSubject.complete();
    }

    private openPaymentSocket(paymentId: string, attempt: number = 0): void {
        const socketUrl = this.getPaymentSocketUrl(paymentId);

        try {
            this.isSocketManuallyClosed = false;
            this.isSocketConnected = false;
            this.paymentSocket = new WebSocket(socketUrl);
        } catch {
            this.retrySocket(paymentId, attempt);
            return;
        }

        this.clearSocketConnectTimer();
        this.socketConnectTimer = setTimeout(() => {
            if (!this.isSocketConnected) {
                this.closeSocket();
                this.retrySocket(paymentId, attempt);
            }
        }, this.SOCKET_CONNECT_TIMEOUT_MS);

        this.paymentSocket.onopen = () => {
            this.isSocketConnected = true;
            this.clearSocketConnectTimer();
        };

        this.paymentSocket.onmessage = (event: MessageEvent) => {
            this.handleIncomingPayload(event.data);
        };

        this.paymentSocket.onerror = () => {
            // Let onclose decide whether to retry socket or fallback.
        };

        this.paymentSocket.onclose = () => {
            this.clearSocketConnectTimer();
            this.paymentSocket = null;
            this.isSocketConnected = false;
            if (!this.isSocketManuallyClosed) {
                this.retrySocket(paymentId, attempt);
            }
        };
    }

    private retrySocket(paymentId: string, attempt: number): void {
        if (
            this.isTerminalStateReached ||
            this.activePaymentId !== paymentId
        ) {
            return;
        }

        if (attempt < this.MAX_SOCKET_RECONNECT_ATTEMPTS) {
            setTimeout(() => {
                if (
                    this.isTerminalStateReached ||
                    this.activePaymentId !== paymentId
                ) {
                    return;
                }
                this.openPaymentSocket(paymentId, attempt + 1);
            }, this.SOCKET_RECONNECT_DELAY_MS);
        }
    }

    private closeSocket(): void {
        this.isSocketManuallyClosed = true;
        this.clearSocketConnectTimer();
        if (this.paymentSocket) {
            this.paymentSocket.close();
            this.paymentSocket = null;
        }
        this.isSocketConnected = false;
    }

    private clearSocketConnectTimer(): void {
        if (this.socketConnectTimer) {
            clearTimeout(this.socketConnectTimer);
            this.socketConnectTimer = null;
        }
    }

    private handleIncomingPayload(rawPayload: string): void {
        try {
            const parsedPayload = JSON.parse(rawPayload || '{}');
            const statusUpdate = this.normalizeStatusPayload(parsedPayload);
            if (!statusUpdate) {
                return;
            }

            this.publishStatus(statusUpdate);
        } catch {
            // Ignore invalid frames.
        }
    }

    private publishStatus(statusUpdate: PaymentStatusUpdate): void {
        if (this.activePaymentId !== statusUpdate.paymentId) {
            return;
        }

        if (this.isTerminalStateReached && this.isTerminalStatus(statusUpdate.status)) {
            return;
        }

        this.statusSubject.next(statusUpdate);

        if (this.isTerminalStatus(statusUpdate.status)) {
            this.isTerminalStateReached = true;
            this.closeSocket();
        }
    }

    private normalizeStatusPayload(payload: any): PaymentStatusUpdate | null {
        const paymentId = payload?.paymentId || payload?.pgOrderId || payload?.orderId || payload?.id;
        const rawStatus = String(payload?.status || '').toUpperCase();
        const transactionId =
            payload?.transactionId ||
            payload?.txnId ||
            payload?.transaction_id ||
            payload?.pgTransactionId ||
            payload?.pgOrderResponse?.transactionId ||
            payload?.data?.transactionId ||
            payload?.data?.txnId ||
            payload?.data?.transaction_id;

        if (!paymentId || !this.isSupportedStatus(rawStatus)) {
            return null;
        }

        return {
            paymentId: String(paymentId),
            status: rawStatus,
            message: payload?.message,
            transactionId: transactionId ? String(transactionId) : undefined,
        };
    }

    private isSupportedStatus(status: string): status is PaymentStatusValue {
        return ['PENDING', 'SUCCESS', 'FAILED', 'EXPIRED', 'CANCELLED'].includes(status);
    }

    private isTerminalStatus(status: PaymentStatusValue): boolean {
        return ['SUCCESS', 'FAILED', 'EXPIRED', 'CANCELLED'].includes(status);
    }

    private getPaymentSocketUrl(paymentId: string): string {
        const apiUrl = new URL(environment.apiURL);
        const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
        const pathName = `${apiUrl.pathname}v1/order/upi-payment/ws/${encodeURIComponent(paymentId)}`;
        return `${protocol}//${apiUrl.host}${pathName}`;
    }
}